export type CertificateAlign = "left" | "center" | "right";
export type CertificateOrientation = "portrait" | "landscape";
export type CertificateElementType = "text" | "logo" | "table" | "chart" | "checklist";
export type CertificateSection = "header" | "body" | "footer";
export type LabelValueStyle = "inline" | "stacked";

export type ReportTable = {
  columns: string[];
  rows: string[][];
  /** When true, render tbody only (no column header row). */
  hideHeader?: boolean;
};

export type ReportChartMark = {
  toothNo: string | number;
  surface?: string;
  color?: string;
  hasSurface?: boolean;
  isTop?: boolean;
};

export type ReportChartPreview = {
  marked?: string[];
  actions?: ReportChartMark[];
};

export type ReportChecklistItem = {
  label: string;
  checked: boolean;
  note?: string;
};

export type ReportChecklistPreview = {
  items: ReportChecklistItem[];
};

export type CertificateElement = {
  id: string;
  type: CertificateElementType;
  label: string;
  section: CertificateSection;
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  visible: boolean;
  fontSize: number;
  fontWeight: number;
  fontFamily?: string;
  italic?: boolean;
  underline?: boolean;
  align: CertificateAlign;
  lineHeight: number;
  letterSpacing?: number;
  textTransform?: "none" | "uppercase" | "capitalize";
  color?: string;
  backgroundColor?: string;
  /** Cell padding for tables (mm). Used especially by amount tables. */
  paddingMm?: number;
  fieldKey?: string;
  pageIndex?: number;
};

export type CertificatePaperSize = "A2" | "A3" | "A4" | "A5" | "halfLetter";

export function normalizeCertificatePaperSize(
  raw: unknown,
  fallback: CertificatePaperSize = "A4"
): CertificatePaperSize {
  const value = String(raw ?? "").trim();
  if (!value) return fallback;
  if (value === "halfLetter" || /^half[\s_-]*letter$/i.test(value)) {
    return "halfLetter";
  }
  const upper = value.toUpperCase();
  if (upper === "A2" || upper === "A3" || upper === "A4" || upper === "A5") {
    return upper as CertificatePaperSize;
  }
  return fallback;
}

export type CertificatePageSettings = {
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
  marginMm: number;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  italic?: boolean;
  underline?: boolean;
  lineHeight: number;
  textAlign: CertificateAlign;
  logoSrc: string;
  /** Optional letterhead image (New Bill Print); separate from clinic logo */
  letterheadSrc?: string;
  logoSizeMm: number;
  fieldSpacing: number;
  paragraphSpacing: number;
  labelValueStyle: LabelValueStyle;
  showSafeArea: boolean;
  headerAlign: CertificateAlign;
  footerText: string;
};

export type CertificateTemplate = {
  id: string;
  name: string;
  page: CertificatePageSettings;
  elements: CertificateElement[];
  pageCount?: number;
};

export type CertificateDesignerStore = {
  templates: CertificateTemplate[];
  activeId: string;
  defaultId: string;
};

export const A2_PORTRAIT = { widthMm: 420, heightMm: 594 } as const;
export const A2_LANDSCAPE = { widthMm: 594, heightMm: 420 } as const;
export const A3_PORTRAIT = { widthMm: 297, heightMm: 420 } as const;
export const A3_LANDSCAPE = { widthMm: 420, heightMm: 297 } as const;
export const A4_PORTRAIT = { widthMm: 210, heightMm: 297 } as const;
export const A4_LANDSCAPE = { widthMm: 297, heightMm: 210 } as const;
export const A5_PORTRAIT = { widthMm: 148, heightMm: 210 } as const;
export const A5_LANDSCAPE = { widthMm: 210, heightMm: 148 } as const;
/** US Half Letter — 5.5 × 8.5 in */
export const HALF_LETTER_PORTRAIT = { widthMm: 139.7, heightMm: 215.9 } as const;
export const HALF_LETTER_LANDSCAPE = { widthMm: 215.9, heightMm: 139.7 } as const;

const PAGE_SIZES = {
  A2: { portrait: A2_PORTRAIT, landscape: A2_LANDSCAPE },
  A3: { portrait: A3_PORTRAIT, landscape: A3_LANDSCAPE },
  A4: { portrait: A4_PORTRAIT, landscape: A4_LANDSCAPE },
  A5: { portrait: A5_PORTRAIT, landscape: A5_LANDSCAPE },
  halfLetter: { portrait: HALF_LETTER_PORTRAIT, landscape: HALF_LETTER_LANDSCAPE },
} as const;

export function getPageSize(
  orientation: CertificateOrientation,
  size: CertificatePageSettings["size"] = "A4"
) {
  const key = size in PAGE_SIZES ? size : "A4";
  return orientation === "landscape"
    ? PAGE_SIZES[key as keyof typeof PAGE_SIZES].landscape
    : PAGE_SIZES[key as keyof typeof PAGE_SIZES].portrait;
}

/** CSS @page size value — named ISO sizes or explicit mm for Half Letter. */
export function getCssAtPageSize(
  orientation: CertificateOrientation,
  size: CertificatePageSettings["size"] = "A4"
) {
  if (size === "A2" || size === "A3" || size === "A4" || size === "A5") {
    return `${size} ${orientation}`;
  }
  const page = getPageSize(orientation, size);
  return `${page.widthMm}mm ${page.heightMm}mm`;
}

/** jsPDF format — always use mm dimensions so Half Letter works. */
export function getJsPdfPageOptions(
  orientation: CertificateOrientation,
  size: CertificatePageSettings["size"] = "A4"
) {
  const page = getPageSize(orientation, size);
  return {
    orientation: (page.widthMm > page.heightMm ? "l" : "p") as "l" | "p",
    unit: "mm" as const,
    format: [page.widthMm, page.heightMm] as [number, number],
    page,
  };
}
